import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import { dateToString } from "utils/dateUtils";
import { Tenant } from "utils/tenants";
import { formatCurrency } from "utils/currency";

const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 65,
        paddingBottom: 65,
        paddingHorizontal: 60,
    },
    header: {
        fontSize: 10,
        flexDirection: "row",
        marginBottom: 10,
        justifyContent: "flex-start",
    },
    image: {
        height: 64,
    },
    companyInfo: {
        marginLeft: 10,
    },
    companyName: {
        fontSize: 14,
        fontWeight: "bold",
    },
    title: {
        fontSize: 20,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },
    customerInfo: {
        fontSize: 10,
        fontWeight: "normal",
        flexDirection: "row",
        marginVertical: 20,
        justifyContent: "space-between",
    },
    customerName: {
        fontSize: 18,
        fontWeight: "bold",
    },
    fileInfo: {
        flexDirection: "row",
    },

    signatureSection: {
        fontSize: 12,
        fontWeight: "normal",
        flexDirection: "row",
        marginTop: 10,
        marginLeft: 10,
        justifyContent: "space-between",
    },

    disclaimerText: {
        fontSize: 8,
        textAlign: "left",
        marginBottom: 10,
    },

    preparedByText: {
        fontSize: 8,
    },

    signature: {
        flex: 1,
        marginLeft: 20,
    },

    balance: {
        flexDirection: "row-reverse",
        marginTop: 10,
    },

    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: "center",
        color: "grey",
    },

    detail: {
        borderWidth: 1,
        borderColor: "gray",
        width: "100%",
    },

    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        borderBottomWidth: 1,
        borderBottomColor: "gray",
    },

    columnHeader: {
        fontSize: 10,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },

    row: {
        flexDirection: "row",
        alignContent: "flex-start",
    },

    cell: {
        fontSize: 9,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },
});

// Create Document Component
const MyDocument = ({ personalInfo, accountingdata, currentUser, balance, isTaxes = false, issuer, currency }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
                {<Image style={styles.image} src={Tenant.getLogo()} />}
                {/* {isTaxes && (
                    <View style={{ ...styles.companyInfo, ...styles.image }}>
                        <Text fixed>Houston</Text>
                        <Text fixed>Multiservices</Text>
                    </View>
                )} */}
                <View style={styles.companyInfo}>
                    {
                        <Text fixed style={styles.companyName}>
                            {issuer?.name}
                        </Text>
                    }
                    <Text fixed>{issuer?.address}</Text>
                    <Text fixed>Phone: {issuer?.phoneNumber}</Text>
                    <Text fixed>Fax: {issuer?.fax}</Text>
                    <Text fixed>Email: {issuer?.email}</Text>
                </View>
                <View style={{ flex: 1 }} />
                <View>
                    <Text fixed style={styles.title}>
                        Statement
                    </Text>
                    <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                        {dateToString(new Date(), false)}
                    </Text>
                </View>
            </View>

            <View style={styles.divider} fixed />

            <View style={styles.customerInfo} fixed>
                <View>
                    <Text fixed style={styles.customerName}>
                        {`${personalInfo.client.firstName} ${personalInfo.client.middleName} ${personalInfo.client.lastName}`}
                    </Text>
                    <Text fixed>{personalInfo.client.address}</Text>
                    <Text fixed>{`${personalInfo.client.city}. ${personalInfo.client.state} ${personalInfo.client.zipCode}`}</Text>
                </View>
                <View style={{ flex: 1 }} />
                <View style={styles.fileInfo}>
                    <Text fixed style={{ fontSize: 12, marginRight: 20 }}>
                        File No
                    </Text>
                    <Text fixed style={{ fontSize: 14 }}>
                        {personalInfo.fileNo}
                    </Text>
                </View>
            </View>

            <View style={styles.detail}>
                <View style={styles.tableHead}>
                    <View style={styles.columnHeader}>
                        <Text>Date</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, textAlign: "right" }}>
                        <Text>Charges</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, textAlign: "right" }}>
                        <Text>Credits</Text>
                    </View>
                    <View style={styles.columnHeader}>
                        <Text>Receipt #</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flex: 2 }}>
                        <Text>Comments</Text>
                    </View>
                </View>
                {accountingdata &&
                    accountingdata.map((item) => (
                        <View key={item.id} style={styles.row}>
                            <View style={styles.cell}>
                                <Text>{dateToString(item.date, false)}</Text>
                            </View>
                            <View style={{ ...styles.cell, textAlign: "right", fontWeight: "bold" }}>
                                <Text>
                                    {item.charge && Number(item.charge) > 0 ? <Money amount={item.charge} currency={currency} /> : ""}
                                </Text>
                            </View>
                            <View style={{ ...styles.cell, textAlign: "right", fontWeight: "bold" }}>
                                <Text>
                                    {item.credit && Number(item.credit) > 0 ? <Money amount={item.credit} currency={currency} /> : ""}
                                </Text>
                            </View>
                            <View style={styles.cell}>
                                <Text>{item.receiptId}</Text>
                            </View>
                            <View style={{ ...styles.cell, flex: 2 }}>
                                <Text>{item.description}</Text>
                            </View>
                        </View>
                    ))}
            </View>

            <View style={styles.divider} />

            <View style={styles.signatureSection}>
                <View style={{ flex: 1 }}>
                    <Text style={styles.disclaimerText}>
                        By signing here, the client agrees with the fee quoted and agrees to pay for the requested services as indicated.
                    </Text>

                    <Text style={styles.disclaimerText}>
                        Al fimar aquí, el cliente está de acuerdo con el costo indicado y para los servicios requeridos y se compromete a
                        hacer pagos según se requiera hasta saldar la cuenta.
                    </Text>

                    <Text style={styles.preparedByText}>Prepared By: {currentUser.name}</Text>
                </View>
                <View style={styles.signature}>
                    <View style={{ ...styles.divider, marginBottom: 7, marginTop: 30 }} />
                    <Text style={{ textAlign: "center", fontSize: 8 }}>Client Signature / Firma del Cliente</Text>
                    <View style={styles.balance}>
                        <Text style={{ fontSize: 14 }}>
                            <Money amount={balance} currency={currency} />
                        </Text>
                        <Text style={{ fontSize: 12, marginRight: 20 }}>Balance:</Text>
                    </View>
                </View>
            </View>

            <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </Page>
    </Document>
);

export default MyDocument;
